import { useEffect, useState } from "react";
import { DataGrid, GridCol } from "@/components/DataGrid";
import { SyncBar } from "@/components/SyncBar";
import { DateRange } from "@/components/DateRange";
import { Drawer } from "@/pages/a/monitoring/articles/detail";
import { Ch } from "@/components/Fa";
import { Tile } from "@/components/Tile";

const C = { positive: "var(--mark-pos)", neutral: "var(--mark-neu)", negative: "var(--mark-neg)" } as const;
const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };

const nf = (x: number) => Intl.NumberFormat("id-ID", { notation: "compact" }).format(x ?? 0);

export default function InfluencersPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [posts, setPosts] = useState<Record<string, any[]>>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 86400e3).toISOString().slice(0, 10); // default: Kemarin (H-1 WIB)
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(today);
  async function load(s2?: string, e2?: string) {
    const r = await fetch(`/api/v1/social/authors?start=${s2 ?? start}&end=${e2 ?? end}`);
    if (r.status === 401) { location.href = "/login"; return; }
    setRows((await r.json()).data ?? []); setOpen(null); setPosts({});
  }
  useEffect(() => { load(); }, []);
  async function toggle(handle: string) {
    if (open === handle) { setOpen(null); return; }
    setOpen(handle);
    if (!posts[handle]) {
      const r = await fetch(`/api/v1/mentions?author=${encodeURIComponent(handle)}&start=${start}&end=${end}&page=1&pageSize=50`);
      const j = await r.json();
      setPosts((p) => ({ ...p, [handle]: j.data ?? [] }));
    }
  }
  const totalPosts = rows.reduce((a, r) => a + (r.posts ?? 0), 0);
  const totalEng = rows.reduce((a, r) => a + (r.likes ?? 0) + (r.comments ?? 0) + (r.shares ?? 0), 0);
  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Top Influencer</h1>
          <div className="avio-t-sm avio-muted">Social · {start} → {end} (WIB) · klik baris untuk melihat post-nya</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); load(s2, e2); }} />
        </div>
      </div>
      <SyncBar wing="social" />
      <div className="avio-tiles">
        <Tile c={1} icon="fa-solid fa-star" label="Akun terpantau" value={rows.length} sub="influencer" />
        <Tile c={3} icon="fa-solid fa-comment-dots" label="Total post" value={totalPosts} sub="dari akun ini" />
        <Tile c={5} icon="fa-solid fa-heart" label="Total interaksi" value={totalEng} sub="like + komentar + share" />
      </div>
      <div className="avio-tablewrap">
      <DataGrid
        cols={[
          { id: "no", header: "#", width: "max", cell: (r: any) => rows.indexOf(r) + 1 },
          { id: "inf", header: "Influencer (klik = post)", width: "min:200", sortable: true, accessor: (r: any) => r.handle, cell: (r: any) => (
            <span style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
              <i className={open === r.handle ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-right"} style={{ fontSize: 10, color: "var(--color-text-muted)" }} />
              <span className="avio-ava">{(r.handle ?? "?").slice(0, 2).toUpperCase()}</span>
              <span style={{ minWidth: 0 }}>
                <b style={{ display: "block" }}>@{r.handle}</b>
                <span className="avio-sub">{r.domain ?? "—"}</span>
              </span>
            </span>) },
          { id: "fol", header: "Followers", width: "max", sortable: true, align: "right", accessor: (r: any) => r.followers ?? 0, cell: (r: any) => nf(r.followers) },
          { id: "post", header: "Post", width: "max", sortable: true, align: "right", accessor: (r: any) => r.posts, cell: (r: any) => r.posts },
          { id: "tone", header: "Komposisi tone", width: "min:120", cell: (r: any) => (
            <span style={{ display: "grid", gap: 3, minWidth: 100 }}>
              <span className="avio-prow__track">
                {r.positive > 0 && <span className="avio-prow__fill" style={{ flex: r.positive, background: C.positive }} />}
                {r.neutral > 0 && <span className="avio-prow__fill" style={{ flex: r.neutral, background: C.neutral }} />}
                {r.negative > 0 && <span className="avio-prow__fill" style={{ flex: r.negative, background: C.negative }} />}
              </span>
              <span className="avio-sub" style={{ fontVariantNumeric: "tabular-nums" }}>
                <span style={{ color: "var(--sent-pos)" }}>+{r.positive}</span> · {r.neutral} · <span style={{ color: "var(--sent-neg)" }}>-{r.negative}</span>
              </span>
            </span>) },
          { id: "likes", header: "Likes", width: "max", sortable: true, align: "right", accessor: (r: any) => r.likes ?? 0, cell: (r: any) => nf(r.likes) },
          { id: "cmt", header: "Cmt", width: "max", sortable: true, align: "right", accessor: (r: any) => r.comments ?? 0, cell: (r: any) => nf(r.comments) },
          { id: "share", header: "Share", width: "max", sortable: true, align: "right", accessor: (r: any) => r.shares ?? 0, cell: (r: any) => nf(r.shares) },
        ] as GridCol<any>[]}
        data={rows}
        onRowClick={(r) => toggle(r.handle)}
        expandedRender={(r) => open === r.handle ? (
          !posts[r.handle] ? <span>memuat…</span> : <>{posts[r.handle].map((p2: any) => (
            <a key={p2.id} className="avio-item" href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDetailId(p2.id); }} style={{ paddingLeft: 0, paddingRight: 0 }}>
              <span className="avio-item__main">
                <span className="avio-item__title">{(p2.title || "(tanpa judul)").slice(0, 110)}</span>
                <span className="avio-item__meta">
                  {new Date(new Date(p2.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(5, 16).replace("T", " ")} ·
                  <Ch t={p2.sourceType} /> {p2.isReply ? "reply" : "post"} ·
                  <i className="fa-solid fa-thumbs-up" /> {nf(p2.likes)} · <i className="fa-solid fa-comment" /> {nf(p2.comments)}
                </span>
              </span>
              {p2.sentimentLabel && <span className="avio-chip" data-sentiment={p2.sentimentLabel}><span className="avio-chip__label">{TONE_ID[p2.sentimentLabel] ?? p2.sentimentLabel}</span></span>}
            </a>
          ))}</>
        ) : null}
        emptyText="Belum ada data influencer — jalankan sync Social dulu."
        minTableWidth={860}
      />
      </div>
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
